import type { ExperimentConfig } from '@/types';

/**
 * Experiment 7: Salts - Their Solubility
 * CHEM-1131 Laboratory
 *
 * Students test 11 cations with 8 anions to observe precipitate formation
 * and derive solubility rules from experimental observations.
 */
const experiment7Config: ExperimentConfig = {
  id: 'exp7',
  number: 7,
  name: 'Salts - Their Solubility',
  description: 'Investigate the solubility of various salt combinations by mixing cation and anion solutions. Observe precipitate formation patterns to derive general solubility rules.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand that salts dissociate into ions in aqueous solution',
    'Observe and identify precipitate formation reactions',
    'Record observations systematically in a grid format',
    'Derive solubility rules from experimental observations',
    'Compare experimental results with standard solubility tables'
  ],
  safetyNotes: [
    'Some solutions are mildly corrosive - avoid skin contact',
    'Heavy metal solutions (Ag+, Cu2+, etc.) should not be disposed of down the drain',
    'Sodium sulfide (Na2S) has a characteristic odor - work in ventilated area',
    'Wear gloves when handling solutions'
  ],
  requiredEquipment: [
    'Large grid paper',
    'Acetate sheet',
    'Tape (to secure acetate to grid)',
    'Dropper bottles (cation solutions, 0.1 M each)',
    'Dropper bottles (anion solutions: 0.1 M NaCl, 0.1 M NaBr, 0.1 M NaI, 0.1 M Na₂S, 0.2 M H₂SO₄, 0.1 M Na₂CO₃, 0.3 M H₃PO₄, 0.1 M NaOH)',
    'Test tubes (for inconclusive confirmatory tests)',
    'Test tube rack',
    'Toothpicks (for swirling reactions)',
    'Wash bottle'
  ],
  parts: [
    {
      id: 1,
      name: 'setup',
      title: 'Part I: Setup and Introduction',
      equipment: ['Grid paper', 'Acetate sheet'],
      initialState: {
        gridPrepared: false,
        safetyAcknowledged: false
      },
      steps: [
        {
          id: 1,
          title: 'Safety Briefing',
          shortDescription: 'Review safety procedures for handling ionic solutions',
          handler: 'handleSafetyAcknowledgement',
          infoContent: {
            title: 'Salt Solubility',
            content: 'When salts dissolve in water, they dissociate into cations and anions. If the product of two ions forms an insoluble compound, a precipitate will appear.',
            whatHappens: 'The concept of salt solubility is introduced: dissolved ions can recombine to form insoluble precipitates when mixed with other ionic solutions.',
            observe: [
              'Review the list of 11 cations and 8 anions to be tested',
              'Understand that a precipitate indicates an insoluble salt has formed',
              'No precipitate means all products remain dissolved (soluble)',
            ],
          }
        },
        {
          id: 2,
          title: 'Prepare Grid',
          shortDescription: 'Set up the 11x8 grid with cations as columns and anions as rows',
          handler: 'handlePrepareGrid',
          infoContent: {
            title: 'Systematic Testing',
            content: 'We will test 11 cations (Al³⁺, Ba²⁺, Ca²⁺, Co²⁺, Cu²⁺, Fe³⁺, Mg²⁺, Mn²⁺, Ni²⁺, Ag⁺, Zn²⁺) with 8 anions (Cl⁻, Br⁻, I⁻, S²⁻, SO₄²⁻, CO₃²⁻, PO₄³⁻, OH⁻). Copy the grid into your lab notebook. Add **one drop** of each cation onto its square, then **one drop** of each anion. Swirl with a **toothpick** if necessary. If a result is **inconclusive**, repeat in a test tube using **2-3 mL of each solution**.',
            whatHappens: 'An 11x8 grid is prepared on grid paper with an acetate overlay to systematically test every cation-anion combination for precipitate formation.',
            observe: [
              'Draw the grid with cations as columns and anions as rows',
              'Copy the grid layout into your lab notebook for recording observations',
              'Ensure all squares are large enough for one drop of each solution',
            ],
          }
        },
        {
          id: 3,
          title: 'Place Acetate Sheet',
          shortDescription: 'Place the acetate sheet over the grid paper',
          handler: 'handlePlaceAcetate'
        }
      ]
    },
    {
      id: 2,
      name: 'chloride',
      title: 'Part II: Chloride Testing',
      equipment: ['0.1 M NaCl solution', 'Cation solutions (0.1 M each)', 'Acetate sheet'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Chloride with All Cations',
          shortDescription: 'Add NaCl solution to each cation square and observe',
          handler: 'handleChlorideTest',
          infoContent: {
            title: 'Chloride Solubility',
            content: 'Most chlorides are soluble. Look for white precipitate with Ag⁺ (silver chloride, AgCl). This is a characteristic test for silver ions.',
            chemicalEquation: 'Ag⁺(aq) + Cl⁻(aq) → AgCl(s) [white]',
            whatHappens: 'NaCl solution is added to each cation square; most chlorides are soluble, but Ag+ produces a distinctive white AgCl precipitate.',
            observe: [
              'Most squares show no precipitate (chlorides are generally soluble)',
              'Ag+ square produces a white curdy precipitate of AgCl',
              'Record whether each reaction produces a precipitate or remains clear',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Chloride Observations',
          shortDescription: 'Note precipitate color and appearance for each reaction',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 3,
      name: 'bromide',
      title: 'Part III: Bromide Testing',
      equipment: ['0.1 M NaBr solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Bromide with All Cations',
          shortDescription: 'Add NaBr solution to each cation square and observe',
          handler: 'handleBromideTest',
          infoContent: {
            title: 'Bromide Solubility',
            content: 'Like chlorides, most bromides are soluble. Silver bromide forms a cream/pale yellow precipitate - slightly different from white AgCl.',
            chemicalEquation: 'Ag⁺(aq) + Br⁻(aq) → AgBr(s) [cream]',
            whatHappens: 'NaBr solution is added to each cation square; most bromides are soluble, but Ag+ produces a cream-colored AgBr precipitate.',
            observe: [
              'Most squares remain clear (bromides are generally soluble)',
              'Ag+ square produces a cream/pale yellow precipitate of AgBr',
              'Compare the AgBr color to AgCl from the previous test (slightly more yellow)',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Bromide Observations',
          shortDescription: 'Note precipitate color and appearance for each reaction',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 4,
      name: 'iodide',
      title: 'Part IV: Iodide Testing',
      equipment: ['0.1 M NaI solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Iodide with All Cations',
          shortDescription: 'Add NaI solution to each cation square and observe',
          handler: 'handleIodideTest',
          infoContent: {
            title: 'Iodide Solubility',
            content: 'Silver iodide forms a bright yellow precipitate. Note the color trend: AgCl (white) → AgBr (cream) → AgI (yellow). This relates to increasing halide size and polarizability.',
            chemicalEquation: 'Ag⁺(aq) + I⁻(aq) → AgI(s) [yellow]',
            whatHappens: 'NaI solution is added to each cation square; silver iodide forms a bright yellow precipitate, completing the color trend of silver halides.',
            observe: [
              'Ag+ square produces a distinctly yellow precipitate of AgI',
              'Note the halide color trend: AgCl (white) to AgBr (cream) to AgI (yellow)',
              'Some transition metal iodides may also show slight color changes',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Iodide Observations',
          shortDescription: 'Note precipitate color and appearance for each reaction',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 5,
      name: 'sulfide',
      title: 'Part V: Sulfide Testing',
      equipment: ['0.1 M Na₂S solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Sulfide with All Cations',
          shortDescription: 'Add Na2S solution to each cation square and observe',
          handler: 'handleSulfideTest',
          infoContent: {
            title: 'Metal Sulfides',
            content: 'Metal sulfides show dramatic color variations! CuS, NiS, CoS, and FeS are black. CdS is bright yellow. ZnS is white. MnS is flesh-colored. These colors are due to d-orbital electron transitions.',
            whatHappens: 'Na2S solution is added to each cation square, producing a variety of vividly colored metal sulfide precipitates due to different d-orbital electronic configurations.',
            observe: [
              'Most transition metal sulfides form black precipitates (CuS, NiS, CoS, FeS)',
              'ZnS forms a white precipitate; MnS forms a distinctive flesh-colored precipitate',
              'Na2S has a characteristic sulfide odor - work in a ventilated area',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Sulfide Observations',
          shortDescription: 'Note the varied precipitate colors',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 6,
      name: 'sulfate',
      title: 'Part VI: Sulfate Testing',
      equipment: ['0.2 M H₂SO₄ solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Sulfate with All Cations',
          shortDescription: 'Add one drop of 0.2 M H₂SO₄ to each cation square and observe',
          handler: 'handleSulfateTest',
          infoContent: {
            title: 'Sulfate Solubility',
            content: 'Most sulfates are soluble! Exceptions include BaSO₄ (white, very insoluble) and CaSO₄ (slightly soluble, may form faint precipitate).',
            chemicalEquation: 'Ba²⁺(aq) + SO₄²⁻(aq) → BaSO₄(s) [white]',
            whatHappens: 'Dilute H2SO4 is added to each cation square; most sulfates are soluble, but Ba2+ forms a dense white BaSO4 precipitate and Ca2+ may form a faint precipitate.',
            observe: [
              'Ba2+ produces a clearly visible white BaSO4 precipitate',
              'Ca2+ may produce a faint white CaSO4 precipitate (slightly soluble)',
              'All other cation squares should remain clear (sulfates are generally soluble)',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Sulfate Observations',
          shortDescription: 'Note precipitate formation for Ba²⁺ and Ca²⁺',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 7,
      name: 'carbonate',
      title: 'Part VII: Carbonate Testing',
      equipment: ['0.1 M Na₂CO₃ solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Carbonate with All Cations',
          shortDescription: 'Add Na2CO3 solution to each cation square and observe',
          handler: 'handleCarbonateTest',
          infoContent: {
            title: 'Carbonate Solubility',
            content: 'Most carbonates are insoluble except Group 1 (Na⁺, K⁺) and NH₄⁺. You should see multiple white precipitates. Some reactions may also produce gas bubbles if the solution is acidic.',
            whatHappens: 'Na2CO3 solution is added to each cation square; most metal carbonates are insoluble and form precipitates, unlike the mostly soluble halides and sulfates.',
            observe: [
              'Most squares produce white precipitates (carbonates are generally insoluble)',
              'Some transition metal carbonates may have slight color tints',
              'Gas bubbles may appear if the cation solution is acidic (CO2 from acid-carbonate reaction)',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Carbonate Observations',
          shortDescription: 'Note precipitate formation for most cations',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 8,
      name: 'phosphate_hydroxide',
      title: 'Part VIII: Phosphate and Hydroxide Testing',
      equipment: ['0.3 M H₃PO₄ solution', '0.1 M NaOH solution', 'Cation solutions (0.1 M each)'],
      initialState: {
        testedCations: [],
        observations: {}
      },
      steps: [
        {
          id: 1,
          title: 'Test Phosphate with All Cations',
          shortDescription: 'Add one drop of 0.3 M H₃PO₄ to each cation square',
          handler: 'handlePhosphateTest',
          infoContent: {
            title: 'Phosphate Solubility',
            content: 'Most phosphates are insoluble except for Group 1 and ammonium phosphates. Expect white precipitates for most transition metal phosphates.',
            whatHappens: 'Dilute H3PO4 is added to each cation square; most phosphates are insoluble and form precipitates similar to carbonates.',
            observe: [
              'Most squares should produce precipitates (phosphates are generally insoluble)',
              'Precipitate colors are mostly white for metal phosphates',
              'Record the appearance carefully for comparison with carbonate results',
            ],
          }
        },
        {
          id: 2,
          title: 'Test Hydroxide with All Cations',
          shortDescription: 'Add NaOH solution to each cation square',
          handler: 'handleHydroxideTest',
          infoContent: {
            title: 'Metal Hydroxides',
            content: 'Hydroxide precipitates show characteristic colors: Cu(OH)₂ is blue, Ni(OH)₂ is green, Co(OH)₂ is rose/blue, Fe(OH)₃ is rust-brown, and Al(OH)₃ is white gelatinous.',
            whatHappens: 'NaOH solution is added to each cation square, producing colorful metal hydroxide precipitates whose colors are characteristic of each metal ion.',
            observe: [
              'Cu(OH)2 forms a light blue precipitate; Ni(OH)2 is pale green',
              'Fe(OH)3 is distinctly rust-brown; Al(OH)3 is white and gelatinous',
              'Co(OH)2 appears rose to blue; Mg(OH)2 and Zn(OH)2 are white',
            ],
          }
        },
        {
          id: 3,
          title: 'Record All Observations',
          shortDescription: 'Complete the observation grid for phosphate and hydroxide',
          handler: 'handleRecordObservations'
        }
      ]
    },
    {
      id: 9,
      name: 'analysis',
      title: 'Part IX: Results Analysis and Rules',
      equipment: ['Completed observation grid', 'Reference solubility table'],
      initialState: {
        derivedRules: [],
        comparisonComplete: false
      },
      steps: [
        {
          id: 1,
          title: 'Analyze Patterns',
          shortDescription: 'Identify patterns in precipitate formation for each anion',
          handler: 'handleAnalyzePatterns',
          infoContent: {
            title: 'Deriving Solubility Rules',
            content: 'Look for patterns: Which anions formed precipitates with most cations? Which formed precipitates with only specific cations? These patterns form the basis of solubility rules.',
            whatHappens: 'The completed grid data is analyzed to find patterns in precipitate formation that reveal general solubility rules for different anion groups.',
            observe: [
              'Halides (Cl-, Br-, I-) formed precipitates primarily with Ag+ only',
              'Carbonates, phosphates, and hydroxides formed precipitates with most cations',
              'Sulfates were mostly soluble except Ba2+ and possibly Ca2+',
            ],
          }
        },
        {
          id: 2,
          title: 'Derive Solubility Rules',
          shortDescription: 'State the general solubility rule for each anion',
          handler: 'handleDeriveRules'
        },
        {
          id: 3,
          title: 'Compare with Reference Table',
          shortDescription: 'Compare your derived rules with the standard solubility table',
          handler: 'handleCompareRules',
          infoContent: {
            title: 'Standard Solubility Rules',
            content: 'General rules: (1) All Na⁺, K⁺, NH₄⁺ salts are soluble. (2) All nitrates are soluble. (3) Most chlorides are soluble except Ag⁺, Pb²⁺, Hg₂²⁺. (4) Most sulfates are soluble except Ba²⁺, Pb²⁺, Ca²⁺. **Cleanup:** Carefully rinse the acetate sheet when all observations have been recorded and return it to the lab cart after drying it off. Please ensure all reagent bottles are returned to the lab cart making sure the lid corresponds to the bottle.',
            whatHappens: 'Experimentally derived solubility rules are compared with the standard solubility table to evaluate accuracy and identify any discrepancies.',
            observe: [
              'Compare your observations with the accepted solubility rules',
              'Identify any discrepancies between your results and the reference table',
              'Note that Group 1 cations (Na+, K+) and nitrates are always soluble',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp7',
    title: 'Salt Solubility Quiz',
    questions: [
      {
        id: 'exp7-q1',
        type: 'mcq' as const,
        question: 'According to general solubility rules, which of the following salts is INSOLUBLE in water?',
        options: [
          { id: 'a', text: 'NaCl' },
          { id: 'b', text: 'KNO₃' },
          { id: 'c', text: 'BaSO₄' },
          { id: 'd', text: 'NH₄Cl' },
        ],
        correctOptionId: 'c',
        explanation: 'Most sulfates are soluble, but BaSO₄ is a notable exception. Barium sulfate is highly insoluble (Ksp \u2248 1.1 × 10⁻\u00B9⁰). Sodium, potassium, and ammonium salts are generally always soluble.',
      },
      {
        id: 'exp7-q2',
        type: 'mcq' as const,
        question: 'Which group of cations forms salts that are almost always soluble regardless of the anion?',
        options: [
          { id: 'a', text: 'Cu\u00B2⁺, Fe\u00B3⁺, Ni\u00B2⁺' },
          { id: 'b', text: 'Na⁺, K⁺, NH₄⁺' },
          { id: 'c', text: 'Ag⁺, Ba\u00B2⁺, Ca\u00B2⁺' },
          { id: 'd', text: 'Al\u00B3⁺, Mg\u00B2⁺, Zn\u00B2⁺' },
        ],
        correctOptionId: 'b',
        explanation: 'Group 1 cations (Na⁺, K⁺) and the ammonium ion (NH₄⁺) form soluble salts with virtually all anions. This is one of the most fundamental solubility rules.',
      },
      {
        id: 'exp7-q3',
        type: 'mcq' as const,
        question: 'When mixing 0.1 M CuSO₄ with 0.1 M Na₂S on the grid, what would you observe?',
        options: [
          { id: 'a', text: 'No reaction (clear solution)' },
          { id: 'b', text: 'A black precipitate of CuS forms' },
          { id: 'c', text: 'A white precipitate of CuS forms' },
          { id: 'd', text: 'A blue precipitate of Cu(OH)₂ forms' },
        ],
        correctOptionId: 'b',
        explanation: 'Copper(II) sulfide (CuS) is extremely insoluble and forms a characteristic black precipitate: Cu\u00B2⁺ + S\u00B2⁻ → CuS\u2193 (black).',
      },
      {
        id: 'exp7-q4',
        type: 'short_answer' as const,
        question: 'Explain why silver chloride (AgCl) is insoluble even though most chloride salts are soluble. How does this relate to the concept of solubility rules having exceptions?',
        sampleAnswer: 'Solubility rules are generalizations based on observed patterns. While most chlorides are soluble (NaCl, KCl, CaCl₂, etc.), AgCl is an exception because the Ag⁺-Cl⁻ ionic bond is strong enough relative to hydration energy that the lattice energy exceeds the energy released by solvation. Similarly, PbCl₂ and Hg₂Cl₂ are also insoluble chlorides. Each solubility rule has specific exceptions that must be memorized.',
      },
      {
        id: 'exp7-q5',
        type: 'calculation' as const,
        question: 'In the solubility grid, you test 11 cations against 8 anions. How many individual mixing tests are performed in total?',
        expectedValue: 88,
        tolerance: 1,
        unit: 'tests',
      },
    ],
  },
};

// Color data for precipitates
export const PRECIPITATE_COLORS = {
  AgCl: { color: '#FFFFFF', description: 'Curdy white precipitate' },
  AgBr: { color: '#FFFDD0', description: 'Cream/pale yellow precipitate' },
  AgI: { color: '#FFFF00', description: 'Bright yellow precipitate' },
  BaSO4: { color: '#FEFEFE', description: 'Fine white precipitate' },
  CaSO4: { color: '#F5F5F5', description: 'Slightly soluble, faint precipitate' },
  CaCO3: { color: '#FFFFFF', description: 'White precipitate' },
  BaCO3: { color: '#FEFEFE', description: 'White precipitate' },
  CuS: { color: '#1A1A1A', description: 'Black precipitate' },
  NiS: { color: '#2D2D2D', description: 'Black precipitate' },
  CoS: { color: '#1F1F1F', description: 'Black precipitate' },
  FeS: { color: '#0D0D0D', description: 'Black precipitate' },
  ZnS: { color: '#FFFAFA', description: 'White precipitate' },
  MnS: { color: '#FFDAB9', description: 'Flesh-colored precipitate' },
  'Cu(OH)2': { color: '#6495ED', description: 'Light blue gelatinous' },
  'Ni(OH)2': { color: '#98FB98', description: 'Pale green precipitate' },
  'Co(OH)2': { color: '#DDA0DD', description: 'Rose to blue precipitate' },
  'Fe(OH)3': { color: '#B7410E', description: 'Rust-colored precipitate' },
  'Al(OH)3': { color: '#F8F8FF', description: 'White gelatinous' },
  'Mg(OH)2': { color: '#FFFAF0', description: 'White precipitate' },
  'Zn(OH)2': { color: '#F5F5F5', description: 'White gelatinous' }
};

// Cation data
export const CATION_DATA = {
  'Al3+': { solutionColor: '#F5F5F5', name: 'Aluminum' },
  'Ba2+': { solutionColor: '#F8F8F8', name: 'Barium' },
  'Ca2+': { solutionColor: '#FAFAFA', name: 'Calcium' },
  'Co2+': { solutionColor: '#FFB6C1', name: 'Cobalt' },
  'Cu2+': { solutionColor: '#4169E1', name: 'Copper' },
  'Fe3+': { solutionColor: '#DAA520', name: 'Iron(III)' },
  'Mg2+': { solutionColor: '#F5F5F5', name: 'Magnesium' },
  'Mn2+': { solutionColor: '#FFE4E1', name: 'Manganese' },
  'Ni2+': { solutionColor: '#90EE90', name: 'Nickel' },
  'Ag+': { solutionColor: '#F8F8F8', name: 'Silver' },
  'Zn2+': { solutionColor: '#FAFAFA', name: 'Zinc' }
};

export default experiment7Config;
